import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { useSnackbar } from 'notistack'
import { getErrorMessage, parseQueryParams } from '../../../utilities/parsers'
import { QueryParams } from '../../../common/types'
import apiUrl from '../../../apis/api.url'
import {
  deleteData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../../apis/api.helpers'
import { useSnackbarManager } from '../../../components/common/snackbar'
import { useAuthStore } from '../../../store/authStore'

const buildUrlWithParams = (baseUrl: string, params: QueryParams) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_INSTITUTIONS, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useAdminUser = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchData(input), {})
}

const customerMode = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.CUSTOMER_MODE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useCustomerMode = (input: QueryParams) => {
  return useQuery(['customer_mode', input], () => customerMode(input), {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_INSTITUTIONS}/${id}`)
}

const adminDetails = async (id: string) => {
  const url = `${apiUrl.ADMIN_INSTITUTIONS}/${id}`
  const response = await getData(url)
  return response
}
export const useAdminDetails = (id: string) => {
  return useQuery(['worker_task', id], () => adminDetails(id), {
    refetchOnWindowFocus: false,
  })
}

export const createIncubation = (input: any) => {
  return postData(`${apiUrl.INCUBATION_CREATE}`, input)
}
export const useCreateIncubation = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async (input: any) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createIncubation(input)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('Academy created successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const createCv = (input: any, id: any) => {
  console.log(input)
  return postData(`${apiUrl.INCUBATION_CREATE}/${id}/upload-pitch-deck`, input)
}
export const useCreateCv = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async ({ formData, id }: { formData: any; id: any }) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createCv(formData, id)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('CV uploaded successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}

const fetchIncubation = async () => {
  const url = apiUrl.INCUBATION_LIST
  const response = await getData(url)
  return response
}

export const useFetchIncubation = () => {
  const userId = useAuthStore((state) => state.userData?.id) // Get user ID from auth store

  return useQuery(['incubation_list', userId], fetchIncubation, {
    select: (data: any) => {
      // Filter data by user ID if user is authenticated
      if (!userId) return { ...data, data: [] } // Return empty if no user

      return {
        ...data,
        data: data.data.filter((item: any) => item.user_id === userId),
      }
    },
    enabled: !!userId, // Only fetch if userId exists
  })
}

export const assignTemplate = (input: any) => {
  return postData(`${apiUrl.TEMPLATE_ASSIGN}`, input)
}
export const useAssignTemplate = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(assignTemplate, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Template Assigned Successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const editCategory = (data: { id: number; input: any }) => {
  return updateData(`${apiUrl.ADMIN_INSTITUTIONS}/${data.id}`, {
    user: data.input,
  })
}

export const useUpdateAdmin = (
  handleSubmission: (data: any) => void // Simplify this if `id` isn't needed separately
) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(editCategory, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)

      enqueueSnackbar('Customer Edited Successfully', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}
